import type { ProgramConfig } from "@/interfaces/program";

function parse(date: string | null) {
  if (!date) return null;
  // Dates arrive as plain YYYY-MM-DD. Parsing that with the Date constructor
  // treats it as UTC midnight, which lands on the previous day for anyone west
  // of Greenwich, so build it as a local date instead.
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function fmt(d: Date) {
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function daysUntil(d: Date) {
  const today = new Date();
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  return Math.round((d.getTime() - start.getTime()) / 86_400_000);
}

// Nothing renders unless HQ has actually set the dates, so an unconfigured
// program doesn't show an empty band.
export default function ProgramCountdown({
  program,
}: {
  program: ProgramConfig;
}) {
  const start = parse(program.starts_on);
  const end = parse(program.ends_on);
  if (!start) return null;

  const away = daysUntil(start);
  const endAway = end ? daysUntil(end) : null;

  let status: string;
  if (away > 0) {
    status = `${away} day${away === 1 ? "" : "s"} to go`;
  } else if (endAway !== null && endAway >= 0) {
    status = "Happening now";
  } else {
    status = "That's a wrap";
  }

  const range = end ? `${fmt(start)} - ${fmt(end)}` : fmt(start);

  return (
    <div className="border-line bg-surface shadow-card flex flex-wrap items-center gap-x-4 gap-y-1 border-2 px-4 py-3">
      <span className="font-display text-caption text-ink-soft font-bold tracking-wide uppercase">
        {program.name} week
      </span>
      <span className="font-display text-subheading text-ink font-bold">
        {range}
      </span>
      <span className="border-line bg-goldenrod text-caption text-ink ml-auto border-2 px-2 py-0.5 font-bold">
        {status}
      </span>
    </div>
  );
}
